import { useEffect, useState } from "react";
import type { Location } from "./types";
import { readJson, writeJson } from "./storage";
import { calibrateLocation } from "./locationCalibration";
import { SHARED_LOC_KEY, readSharedLocation, writeSharedLocation } from "./sharedLocation";
import {
  checkRelocationSuggestion,
  detectLocationFromIp,
  dismissIpGeoSuggestion,
  rememberAcceptedLocation
} from "./autoGeo";

/** Persist a location to BOTH this app's store and the cross-app shared widget key. */
function persistLocation(loc: Location): void {
  writeJson("location", loc);
  writeSharedLocation(loc);
}

export function useLocationState() {
  // Prefer the cross-app shared location (set by the landing widget or a prior
  // app session) so the user's choice carries over between /prayer and landing.
  const [location, setLocationState] = useState<Location | null>(
    () => readSharedLocation() ?? readJson<Location | null>("location", null)
  );
  const [suggestedLocation, setSuggestedLocation] = useState<Location | null>(null);

  // On mount, mirror the hydrated location into BOTH stores so they stay in sync
  // whether it came from the shared widget key or this app's own key.
  useEffect(() => {
    if (!location) return;
    writeJson("location", location);
    if (!localStorage.getItem(SHARED_LOC_KEY)) writeSharedLocation(location);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live cross-tab sync: if the location changes in another tab (e.g. the user
  // sets it on the landing page in a different tab), reflect it here.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key !== SHARED_LOC_KEY || !e.newValue) return;
      const shared = readSharedLocation();
      if (!shared) return;
      const fixed = calibrateLocation(shared);
      setLocationState(fixed);
      writeJson("location", fixed); // mirror locally without re-writing the shared key (avoids loops)
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (location) return;
    void (async () => {
      const loc = await detectLocationFromIp();
      if (!loc || cancelled) return;
      const fixed = calibrateLocation(loc);
      setLocationState(fixed);
      persistLocation(fixed);
    })();
    return () => {
      cancelled = true;
    };
  }, [location]);

  useEffect(() => {
    if (!location) return;
    const fixed = calibrateLocation(location);
    const changed =
      fixed.timezone !== location.timezone ||
      fixed.city !== location.city ||
      fixed.country !== location.country ||
      fixed.latitude !== location.latitude ||
      fixed.longitude !== location.longitude;

    if (!changed) return;
    setLocationState(fixed);
    persistLocation(fixed);
  }, [location]);

  useEffect(() => {
    let cancelled = false;
    if (!location) return;
    void (async () => {
      const suggested = await checkRelocationSuggestion(location);
      if (!cancelled) setSuggestedLocation(suggested);
    })();
    return () => {
      cancelled = true;
    };
  }, [location]);

  const setLocation = (next: Location) => {
    const fixed = calibrateLocation(next);
    setLocationState(fixed);
    persistLocation(fixed);
  };

  const acceptSuggestedLocation = () => {
    const suggested = suggestedLocation;
    if (!suggested) return;
    const fixed = calibrateLocation(suggested);
    setLocationState(fixed);
    persistLocation(fixed);
    rememberAcceptedLocation(fixed);
    setSuggestedLocation(null);
  };

  const dismissSuggestedLocation = (hours = 24) => {
    dismissIpGeoSuggestion(hours);
    setSuggestedLocation(null);
  };

  return {
    location,
    setLocation,
    suggestedLocation,
    acceptSuggestedLocation,
    dismissSuggestedLocation
  };
}
